import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Loader2, CheckCircle2, X } from 'lucide-react';
import { IconTicket } from '@tabler/icons-react';
import { searchDefeitos } from '../../new-os/services/defeitoService';
import { fetchAdhocOsContext, createAdhocRegisteredOs } from '../services/checkoutViewService';
import { useComposerVoiceRecorder } from '../../board-chat/components/ComposerVoiceRecorder';
import { BoardChatAiWriteMenu } from '../../board-chat/components/BoardChatAiWriteMenu';
import '../../board-chat/components/BoardChatPanel.css';
import '../../new-os/components/OpenOsV2MockOverlay.css';
import './CheckoutAdhocOsCreateModal.css';

const MIN_OBS = 15;

function formatAdhocDateTimeParts(val) {
  if (val == null || val === '') return { date: '—', time: '—' };
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '—' };
  const dd = String(d.getDate()).padStart(2, '0');
  const MM = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return { date: `${dd}/${MM}/${yyyy}`, time: `${hh}:${mm}` };
}

function formatMeterValue(val) {
  if (val == null || val === '' || Number.isNaN(Number(val))) return '—';
  return Math.round(Number(val)).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

export function CheckoutAdhocOsCreateModal({ open, flow, onClose, onSuccess, onGoToFechamento }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [requester, setRequester] = useState('');
  const [technicalReport, setTechnicalReport] = useState('');
  const [adhocOperationCode, setAdhocOperationCode] = useState('');
  const [meters, setMeters] = useState([]);
  const [startedAt, setStartedAt] = useState(null);
  const [finishedAt, setFinishedAt] = useState(null);
  const [defeitoQuery, setDefeitoQuery] = useState('');
  const [defeito, setDefeito] = useState('');
  const [defeitoCodigo, setDefeitoCodigo] = useState('');
  const [defeitoHits, setDefeitoHits] = useState([]);
  const [defeitoSearching, setDefeitoSearching] = useState(false);
  const [obs, setObs] = useState('');
  const [novaOs, setNovaOs] = useState(null);
  const [obsVoiceError, setObsVoiceError] = useState('');
  const [obsVoiceAnchor, setObsVoiceAnchor] = useState(null);
  const [obsAiOpen, setObsAiOpen] = useState(false);
  const [obsAiAnchor, setObsAiAnchor] = useState(null);
  const searchTimerRef = useRef(null);
  const searchSeqRef = useRef(0);
  const obsRef = useRef(null);
  const obsBodyRef = useRef(null);
  const hostRef = useRef(null);

  const obsVoice = useComposerVoiceRecorder({
    onError: (msg) => setObsVoiceError(msg || 'Falha no microfone'),
  });
  const obsVoiceRef = useRef(obsVoice);
  obsVoiceRef.current = obsVoice;

  const equipment = flow?.equipment;
  const codEquip = equipment?.codprod || equipment?.codProd || null;
  const operationCode = flow?.operationCode || '';

  const stopObsVoice = useCallback(() => {
    obsVoiceRef.current.cancel();
    setObsVoiceAnchor(null);
    setObsVoiceError('');
  }, []);

  const closeObsAi = useCallback(() => {
    setObsAiOpen(false);
    setObsAiAnchor(null);
  }, []);

  useEffect(() => {
    if (!open || !flow?.operationCode) return undefined;
    setLoading(true);
    setError('');
    setReason('');
    setRequester('');
    setTechnicalReport('');
    setAdhocOperationCode('');
    setMeters([]);
    setStartedAt(null);
    setFinishedAt(null);
    setDefeitoQuery('');
    setDefeito('');
    setDefeitoCodigo('');
    setDefeitoHits([]);
    setObs('');
    setNovaOs(null);
    stopObsVoice();
    closeObsAi();

    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('Sessão expirada');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    fetchAdhocOsContext(token, flow.operationCode)
      .then((data) => {
        if (cancelled) return;
        setReason(data?.reason || '—');
        setRequester(data?.requester || '');
        setTechnicalReport(data?.technicalReport || '');
        setAdhocOperationCode(data?.operationCode || flow.operationCode || '');
        setMeters(Array.isArray(data?.meters) ? data.meters : []);
        setStartedAt(data?.startedAt ?? null);
        setFinishedAt(data?.finishedAt ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Erro ao carregar atendimento avulso');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      stopObsVoice();
      closeObsAi();
    };
  }, [open, flow?.operationCode, stopObsVoice, closeObsAi]);

  useEffect(() => {
    if (!open || !novaOs) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, novaOs]);

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(searchTimerRef.current);
    const q = defeitoQuery.trim();
    if (defeito || q.length < 3) {
      setDefeitoHits([]);
      setDefeitoSearching(false);
      return undefined;
    }
    setDefeitoSearching(true);
    const seq = ++searchSeqRef.current;
    searchTimerRef.current = setTimeout(async () => {
      try {
        const result = await searchDefeitos(q, codEquip);
        if (seq !== searchSeqRef.current) return;
        const items = (result?.items || []).map((d) => ({
          codigo: String(d.codigo || d.CODIGO || '').trim(),
          nome: String(d.nome || d.NOME || '').trim(),
        })).filter((d) => d.nome);
        setDefeitoHits(items);
      } catch {
        if (seq === searchSeqRef.current) setDefeitoHits([]);
      } finally {
        if (seq === searchSeqRef.current) setDefeitoSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [open, defeitoQuery, defeito, codEquip]);

  const selectDefeito = useCallback((item) => {
    if (!item?.nome) return;
    setDefeito(item.nome);
    setDefeitoQuery(item.nome);
    setDefeitoCodigo(item.codigo || '');
    setDefeitoHits([]);
    setTimeout(() => obsRef.current?.focus?.(), 80);
  }, []);

  const clearDefeito = useCallback(() => {
    setDefeito('');
    setDefeitoQuery('');
    setDefeitoCodigo('');
    setDefeitoHits([]);
    setObs('');
    stopObsVoice();
    closeObsAi();
  }, [stopObsVoice, closeObsAi]);

  const handleObsVoiceClick = useCallback(() => {
    const rec = obsVoiceRef.current;
    if (rec.busy) return;
    closeObsAi();
    setObsVoiceError('');
    if (rec.recording) {
      rec.toggle();
      setObsVoiceAnchor(null);
      return;
    }
    const wrap = obsBodyRef.current;
    const r = (wrap || obsRef.current)?.getBoundingClientRect?.();
    if (r) {
      setObsVoiceAnchor({
        top: r.bottom + 6,
        left: Math.max(12, r.right - 268),
      });
    }
    rec.toggle();
  }, [closeObsAi]);

  const handleObsAiClick = useCallback(() => {
    stopObsVoice();
    const btn = hostRef.current?.querySelector?.('[data-checkout-adhoc-obs-ai]');
    const r = btn?.getBoundingClientRect?.();
    if (r) {
      setObsAiAnchor({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
    }
    setObsAiOpen((v) => !v);
  }, [stopObsVoice]);

  const handleObsTranscribe = async () => {
    setObsVoiceError('');
    try {
      const rec = obsVoiceRef.current;
      if (!rec.speechSupported) {
        await rec.cancel();
        setObsVoiceError('Transcrição não suportada neste navegador.');
        return;
      }
      const text = String((await rec.finishTranscript()) || '').trim();
      if (!text) {
        setObsVoiceError('Não foi possível obter o texto desta gravação.');
        return;
      }
      setObs((prev) => {
        const cur = String(prev || '').trim();
        return cur ? `${cur} ${text}` : text;
      });
      setObsVoiceAnchor(null);
    } catch (e) {
      setObsVoiceError(e?.message || 'Não foi possível obter o texto desta gravação.');
    }
  };

  const defeitoOk = !!defeito && obs.trim().length >= MIN_OBS;
  const obsLen = obs.trim().length;
  const contadorObsClass = `oosv2-obs-counter${obsLen >= MIN_OBS ? ' is-ok' : ' is-short'}`;

  const handleSubmit = async () => {
    if (!defeitoOk || !flow) return;
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('Sessão expirada');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const data = await createAdhocRegisteredOs(token, {
        operationCode: flow.operationCode,
        contrato: flow.contrato || equipment?.codigo || '',
        serie: flow.serie || equipment?.serie || '',
        codigoDefeito: defeitoCodigo,
        motivoObsDefeito: obs.trim(),
      });
      const num = data?.osNumber;
      if (!num) throw new Error('OS criada sem número retornado');
      setNovaOs(String(num));
      onSuccess?.(String(num), flow);
    } catch (err) {
      setError(err?.message || 'Erro ao abrir OS');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !flow) return null;

  const serie = equipment?.serie || flow.serie || '';
  const patrimonio = equipment?.patrimonio || '';
  const produto = equipment?.produto || '';
  const seriePat = [serie, patrimonio].filter(Boolean).join(' • ');
  const voiceActive = obsVoice.recording || obsVoice.busy;
  const startedParts = formatAdhocDateTimeParts(startedAt);
  const finishedParts = formatAdhocDateTimeParts(finishedAt);

  const overlay = (
    <div
      className={`open-os-v2-mock-overlay checkout-adhoc-os-create-overlay${novaOs ? ' is-success-locked' : ' is-flow-locked'}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-adhoc-os-create-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div
        ref={hostRef}
        className="open-os-v2-mock-host"
        data-checkout-adhoc-os-create-host="1"
      >
        <div className="oosv2-page">
          <div className="oosv2-shell">
            <div className="oosv2-card">
              <div className="oosv2-flow-header">
                <div className="oosv2-flow-header-top">
                  <div className="oosv2-flow-brand">
                    <div
                      id="checkout-adhoc-os-create-title"
                      className="oosv2-flow-context"
                    >
                      {operationCode
                        ? `Atendimento avulso ${operationCode}`
                        : 'Atendimento avulso'}
                    </div>
                  </div>
                  <div className="oosv2-flow-actions">
                    {!novaOs ? (
                      <button
                        type="button"
                        className="oosv2-flow-close"
                        onClick={onClose}
                        aria-label="Fechar"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="oosv2-flow-progress" aria-hidden="true" />
              </div>

              <div className="oosv2-flow-body">
                {loading ? (
                  <div className="checkout-adhoc-os-create-loading">
                    <Loader2 size={18} className="checkout-adhoc-os-create-spin" aria-hidden />
                    <span>Carregando atendimento…</span>
                  </div>
                ) : null}

                {!loading && novaOs ? (
                  <div className="checkout-adhoc-os-create-success">
                    <CheckCircle2 size={32} strokeWidth={1.75} aria-hidden />
                    <p className="checkout-adhoc-os-create-success-title">OS aberta com sucesso</p>
                    <p className="checkout-adhoc-os-create-success-os">Nº {novaOs}</p>
                    <button
                      type="button"
                      className="oosv2-flow-continue-btn"
                      onClick={() => {
                        if (typeof onGoToFechamento === 'function') {
                          onGoToFechamento(String(novaOs), flow);
                        } else {
                          onClose?.();
                        }
                      }}
                    >
                      Ir para fechamento
                    </button>
                  </div>
                ) : null}

                {!loading && !novaOs ? (
                  <>
                    <div className="checkout-adhoc-os-create-section">
                      <div className="oosv2-equip-head">
                        <div className="oosv2-equip-head-title">Abertura da OS</div>
                        <div className="oosv2-equip-head-sub">
                          Confirme o equipamento e informe o defeito.
                        </div>
                      </div>

                      <div className="checkout-adhoc-os-create-equip-grid">
                        <div className="checkout-adhoc-os-create-field">
                          <div className="oosv2-equip-kicker">Equipamento</div>
                          <div className="checkout-adhoc-os-create-field-value">
                            {produto || '—'}
                          </div>
                        </div>
                        <div className="checkout-adhoc-os-create-field">
                          <div className="oosv2-equip-kicker">Série / Patrimônio</div>
                          <div className="checkout-adhoc-os-create-field-value checkout-adhoc-os-create-field-value--mono">
                            {seriePat || '—'}
                          </div>
                        </div>
                      </div>

                      <div className="checkout-adhoc-os-create-reason-block">
                        <div className="oosv2-equip-kicker">Motivo informado pelo técnico</div>
                        <input
                          type="text"
                          className="checkout-adhoc-os-create-reason-input"
                          value={reason || '—'}
                          readOnly
                          tabIndex={-1}
                          aria-label="Motivo informado pelo técnico"
                        />
                      </div>

                      <div className="checkout-adhoc-os-create-closing-preview">
                        <div className="checkout-adhoc-os-create-meta-row">
                          <div className="checkout-adhoc-os-create-field">
                            <div className="oosv2-equip-kicker">Solicitante</div>
                            <div className="checkout-adhoc-os-create-field-value">
                              {requester || '—'}
                            </div>
                          </div>
                          <div className="checkout-adhoc-os-create-field checkout-adhoc-os-create-field--attend">
                            <div className="oosv2-equip-kicker">Tempo de atendimento</div>
                            <div className="checkout-adhoc-os-create-attend-row">
                              <span className="checkout-adhoc-os-create-field-value">
                                {startedParts.date}
                              </span>
                              <span className="checkout-adhoc-os-create-attend-time">
                                {startedParts.time}
                              </span>
                              <span className="checkout-adhoc-os-create-attend-sep" aria-hidden>
                                →
                              </span>
                              <span className="checkout-adhoc-os-create-field-value">
                                {finishedParts.date}
                              </span>
                              <span className="checkout-adhoc-os-create-attend-time">
                                {finishedParts.time}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="checkout-adhoc-os-create-field checkout-adhoc-os-create-field--laudo">
                          <div className="oosv2-equip-kicker">Laudo técnico</div>
                          <div
                            className="checkout-adhoc-os-create-laudo"
                            aria-label="Laudo técnico"
                          >
                            <div className="checkout-adhoc-os-create-laudo-row">
                              <span className="checkout-adhoc-os-create-laudo-label">OS Avulsa:</span>
                              <span className="checkout-adhoc-os-create-laudo-value checkout-adhoc-os-create-field-value--mono">
                                {adhocOperationCode || operationCode || '—'}
                              </span>
                            </div>
                            <div className="checkout-adhoc-os-create-laudo-row">
                              <span className="checkout-adhoc-os-create-laudo-label">Laudo:</span>
                              <span className="checkout-adhoc-os-create-laudo-value">
                                {technicalReport || '—'}
                              </span>
                            </div>
                            <div className="checkout-adhoc-os-create-laudo-row checkout-adhoc-os-create-laudo-row--meters">
                              <span className="checkout-adhoc-os-create-laudo-label">Contadores:</span>
                              <div className="checkout-adhoc-os-create-laudo-meters">
                                {meters.length > 0 ? (
                                  meters.map((m, i) => (
                                    <div
                                      key={`${m.name}-${i}`}
                                      className="checkout-adhoc-os-create-laudo-meter"
                                    >
                                      <span>{m.name || '—'}</span>
                                      <span className="checkout-adhoc-os-create-laudo-meter-sep">
                                        {' '}
                                        -{' '}
                                      </span>
                                      <span className="checkout-adhoc-os-create-field-value--mono">
                                        {formatMeterValue(m.value)}
                                      </span>
                                    </div>
                                  ))
                                ) : (
                                  <span className="checkout-adhoc-os-create-laudo-value">—</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="checkout-adhoc-os-create-section">
                      {!defeito ? (
                        <div className="checkout-adhoc-os-create-defeito-wrap">
                          <div className="oosv2-defeito-label-row">
                            <div className="oosv2-defeito-label">Informe o defeito</div>
                            <p className="oosv2-defeito-hint">
                              (Digite os três primeiros caracteres do defeito).
                            </p>
                          </div>
                          <input
                            type="text"
                            className="oosv2-defeito-input checkout-adhoc-os-create-defeito-input"
                            value={defeitoQuery}
                            onChange={(e) =>
                              setDefeitoQuery(e.target.value.replace(/\s+/g, ' ').trimStart())
                            }
                            placeholder="Comece a digitar: atola, barulho, mancha…"
                          />
                          {defeitoSearching ? (
                            <p className="checkout-adhoc-os-create-hint">Buscando defeitos…</p>
                          ) : null}
                          {defeitoQuery.trim().length >= 3 && defeitoHits.length > 0 ? (
                            <div className="oosv2-defeito-results">
                              {defeitoHits.map((d, i) => (
                                <div
                                  key={`${d.codigo}-${i}`}
                                  className="oosv2-defeito-result-row"
                                  onClick={() => selectDefeito(d)}
                                >
                                  {d.nome}
                                </div>
                              ))}
                            </div>
                          ) : null}
                          {defeitoQuery.trim().length >= 3 &&
                          !defeitoSearching &&
                          defeitoHits.length === 0 ? (
                            <div className="checkout-adhoc-os-create-empty">
                              Nenhum defeito encontrado.
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {defeito ? (
                        <div className="checkout-adhoc-os-create-defeito-selected">
                          <div className="checkout-adhoc-os-create-defeito-selected-row">
                            <div className="oosv2-equip-kicker">Defeito selecionado</div>
                            <div className="oosv2-defeito-chip">
                              <span className="oosv2-defeito-chip-label">{defeito}</span>
                              <button
                                type="button"
                                className="oosv2-defeito-chip-clear"
                                onClick={clearDefeito}
                                aria-label="Remover defeito"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                          <div className="oosv2-obs-block">
                            <div className="oosv2-obs-counter-row">
                              <span className={contadorObsClass}>
                                {obsLen}/{MIN_OBS}
                              </span>
                            </div>
                            <div className="oosv2-obs-body" ref={obsBodyRef}>
                              <textarea
                                ref={obsRef}
                                className="oosv2-obs-textarea checkout-adhoc-os-create-obs"
                                placeholder="Descreva o que o cliente relatou… (obrigatório, mín. 15 caracteres)"
                                value={obs}
                                onChange={(e) => setObs(e.target.value)}
                              />
                              <div className="oosv2-obs-tools">
                                <button
                                  type="button"
                                  className={`oosv2-obs-tool-btn${voiceActive ? ' is-recording' : ''}`}
                                  data-checkout-adhoc-obs-voice="1"
                                  aria-label="Fala para texto"
                                  onClick={handleObsVoiceClick}
                                >
                                  <span className="oosv2-obs-tool-tip">
                                    Grave em voz e tenha o texto transcrito aqui
                                  </span>
                                  <svg className="oosv2-obs-tool-ico" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                    <rect x="6" y="2" width="4" height="7" rx="2" stroke="currentColor" strokeWidth="1.5" />
                                    <path d="M4 8a4 4 0 008 0M8 12v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  </svg>
                                  <span>Fala para texto</span>
                                </button>
                                <button
                                  type="button"
                                  className={`oosv2-obs-tool-btn${obsAiOpen ? ' is-on' : ''}`}
                                  data-checkout-adhoc-obs-ai="1"
                                  aria-label="Texto com IA"
                                  onClick={handleObsAiClick}
                                >
                                  <span className="oosv2-obs-tool-tip">
                                    Orya pode te ajudar a deixar o texto melhor
                                  </span>
                                  <svg className="oosv2-obs-tool-ico" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                    <path d="M8 2l1.3 3.2L12.5 6.5 9.3 7.8 8 11 6.7 7.8 3.5 6.5 6.7 5.2 8 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                                    <path d="M12.5 11.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6.6-1.4z" fill="currentColor" />
                                  </svg>
                                  <span>Texto com IA</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {error ? (
                      <div className="checkout-adhoc-os-create-error">{error}</div>
                    ) : null}

                    <div className="checkout-adhoc-os-create-actions">
                      <button
                        type="button"
                        className="oosv2-flow-back-btn"
                        onClick={onClose}
                        disabled={submitting}
                      >
                        Cancelar
                      </button>
                      <div className="checkout-adhoc-os-create-actions-spacer" />
                      <button
                        type="button"
                        className={`oosv2-flow-continue-btn${!defeitoOk || submitting ? ' is-disabled' : ''}`}
                        disabled={!defeitoOk || submitting}
                        onClick={handleSubmit}
                      >
                        {submitting ? (
                          <>
                            <Loader2 size={14} className="checkout-adhoc-os-create-spin" aria-hidden />
                            {' '}Abrindo OS…
                          </>
                        ) : (
                          <>
                            <IconTicket size={16} stroke={1.7} aria-hidden />
                            Abrir OS
                          </>
                        )}
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {voiceActive || obsVoiceError ? (
        <div
          className="oosv2-agenda-voice-pop"
          style={
            obsVoiceAnchor
              ? { top: `${obsVoiceAnchor.top}px`, left: `${obsVoiceAnchor.left}px` }
              : undefined
          }
          role="status"
          aria-live="polite"
        >
          {voiceActive ? (
            <>
              <span className={`oosv2-agenda-voice-pulse${obsVoice.busy ? ' is-busy' : ''}`} aria-hidden />
              <span className="oosv2-agenda-voice-timer">
                {`${Math.floor((obsVoice.elapsedSec || 0) / 60)}:${String((obsVoice.elapsedSec || 0) % 60).padStart(2, '0')}`}
              </span>
              <span className="oosv2-agenda-voice-label">
                {obsVoice.busy ? 'Processando…' : 'Gravando…'}
              </span>
              <button
                type="button"
                className="oosv2-agenda-voice-transcribe"
                disabled={obsVoice.busy}
                onClick={handleObsTranscribe}
              >
                Transcrever
              </button>
              <button
                type="button"
                className="oosv2-agenda-voice-cancel"
                disabled={obsVoice.busy}
                aria-label="Cancelar gravação"
                onClick={stopObsVoice}
              >
                <X size={13} strokeWidth={2} />
              </button>
            </>
          ) : null}
          {obsVoiceError ? <div className="oosv2-agenda-voice-error">{obsVoiceError}</div> : null}
        </div>
      ) : null}

      {obsAiOpen && obsAiAnchor ? (
        <div
          className="oosv2-obs-ai-anchor"
          style={{
            top: `${obsAiAnchor.top}px`,
            left: `${obsAiAnchor.left}px`,
            width: `${obsAiAnchor.width}px`,
            height: `${obsAiAnchor.height}px`,
          }}
        >
          <BoardChatAiWriteMenu
            open
            hideTrigger
            draft={obs}
            applyLabel="Usar nas observações"
            onOpenChange={(next) => {
              if (!next) closeObsAi();
            }}
            onApply={(text) => {
              const next = String(text || '').trim();
              if (next) setObs(next);
              closeObsAi();
            }}
          />
        </div>
      ) : null}
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
}

export default CheckoutAdhocOsCreateModal;
